/**
 * OutboundDetailDialog — hand-rolled modal for outbound message detail.
 * Ported from v0 mail-dialogs.tsx OutboundDetailDialog.
 * Read-only for status=gesendet; editable + save/send for others.
 */
import { useEffect, useState } from 'react';
import { Save, Send, X, Clock, PenLine, CheckCircle2 } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { sanitizeEmailHtml } from '../../../lib/sanitize-email-html';
import { mailTeamMarkerName } from '../mail-visibility';
import { MailTeamBadge } from './MailTeamBadge';
import { OUT_STATUS } from '../mock';
import type { AusgangMail } from '../types';

function HerkunftBadge({ herkunft }: { herkunft: 'ki' | 'manuell' }) {
  return (
    <Badge variant={herkunft === 'ki' ? 'info' : 'neutral'}>
      {herkunft === 'ki' ? '✦ KI-Entwurf' : '✎ Manuell'}
    </Badge>
  );
}

interface OutboundDetailDialogProps {
  mail: AusgangMail | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSend: (id: string) => void;
  onSave: (id: string) => void;
  /**
   * N2: the sent message's HTML body (comm.body_html, cid: logos already inlined
   * server-side). When present on a read-only (sent) message it renders the actual
   * formatting + embedded logos, sanitized. Absent → the metadata-only fallback.
   */
  bodyHtml?: string | null;
}

export function OutboundDetailDialog({
  mail,
  open,
  onOpenChange,
  onSend,
  onSave,
  bodyHtml = null,
}: OutboundDetailDialogProps) {
  const [empfaenger, setEmpfaenger] = useState('');
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');

  useEffect(() => {
    if (open && mail) {
      setEmpfaenger(mail.empfaenger);
      setBetreff(mail.betreff);
      setText(
        mail.text ?? 'Sehr geehrte Damen und Herren,\n\n…\n\nFreundliche Grüsse\nLara Meier, Brokly',
      );
    }
  }, [open, mail]);

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onOpenChange(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  if (!open || !mail) return null;

  const readOnly = mail.status === 'gesendet';
  const s = OUT_STATUS[mail.status];
  // The row that opened this dialog carries the same marker; losing it here would read as
  // "restricted in the list only".
  const restrictedTeam = mailTeamMarkerName(mail.restrictedToTeam);

  const history = [
    { Icon: PenLine, text: 'Entwurf erstellt', time: 'vor 3 Tagen' },
    { Icon: Send, text: 'Zum Versand freigegeben', time: 'vor 2 Tagen' },
    { Icon: CheckCircle2, text: `Gesendet an ${mail.empfaenger}`, time: mail.zeit },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg overflow-hidden rounded-lg border bg-card shadow-lg">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 border-b p-4">
          <div className="flex flex-wrap items-center gap-2 pr-4">
            <h2 className="text-pretty font-semibold text-foreground">{mail.betreff}</h2>
            <Badge variant={s.tone}>{s.label}</Badge>
            <HerkunftBadge herkunft={mail.herkunft} />
            {restrictedTeam != null && <MailTeamBadge team={restrictedTeam} size="md" />}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            aria-label="Schliessen"
            className="size-7 shrink-0"
          >
            <X className="size-4" />
          </Button>
        </div>

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto p-4">
          {readOnly ? (
            <div className="flex flex-col gap-4">
              <dl className="divide-y divide-border overflow-hidden rounded-md border">
                {[
                  { label: 'Empfänger', value: mail.empfaenger },
                  { label: 'Kunde', value: mail.kunde },
                  { label: 'Versand', value: mail.zeit },
                ].map((row) => (
                  <div
                    key={row.label}
                    className="flex items-baseline justify-between gap-4 px-3 py-2.5"
                  >
                    <dt className="text-sm text-muted-foreground">{row.label}</dt>
                    <dd className="text-sm font-medium text-foreground">{row.value}</dd>
                  </div>
                ))}
              </dl>

              {/* N2: the actual sent message — real HTML formatting + embedded logos,
                  sanitized (scripts/handlers/remote embeds stripped; cid: logos were
                  already inlined to data URIs server-side). */}
              {bodyHtml && (
                <>
                  <div className="border-t border-border" />
                  <section className="flex flex-col gap-2">
                    <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Nachricht
                    </h3>
                    <div
                      data-testid="outbound-body-html"
                      className="overflow-x-auto rounded-md border bg-muted/40 p-3 text-sm leading-relaxed text-foreground/90 [&_a]:text-primary [&_a]:underline"
                      dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(bodyHtml) }}
                    />
                  </section>
                </>
              )}

              <div className="border-t border-border" />

              <section className="flex flex-col gap-2">
                <h3 className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <Clock className="size-3.5" />
                  Dokumenten-Verlauf
                </h3>
                <ol className="flex flex-col">
                  {history.map((h, i) => {
                    const Icon = h.Icon;
                    const isLast = i === history.length - 1;
                    return (
                      <li key={i} className="relative flex gap-3 pb-3 last:pb-0">
                        {!isLast && (
                          <span
                            aria-hidden
                            className="absolute bottom-0 left-[11px] top-6 w-px bg-border"
                          />
                        )}
                        <span className="relative z-10 flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground ring-2 ring-popover">
                          <Icon className="size-3.5" />
                        </span>
                        <div className="flex min-w-0 flex-1 items-baseline justify-between gap-3 pt-0.5">
                          <span className="text-sm text-foreground/90">{h.text}</span>
                          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                            {h.time}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </section>
            </div>
          ) : (
            <form
              id="outbound-form"
              className="flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                onSend(mail.id);
              }}
            >
              {mail.status === 'fehler' && mail.fehlerGrund && (
                <p className="rounded-md border border-sem-alert/30 bg-sem-alert-bg px-3 py-2 text-sm text-sem-alert">
                  {mail.fehlerGrund}
                </p>
              )}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="ob-to" className="text-xs text-muted-foreground">Empfänger</label>
                <input
                  id="ob-to"
                  type="email"
                  value={empfaenger}
                  onChange={(e) => setEmpfaenger(e.target.value)}
                  className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="ob-betreff" className="text-xs text-muted-foreground">Betreff</label>
                <input
                  id="ob-betreff"
                  type="text"
                  value={betreff}
                  onChange={(e) => setBetreff(e.target.value)}
                  className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="ob-text" className="text-xs text-muted-foreground">Nachricht</label>
                <textarea
                  id="ob-text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  className="min-h-44 resize-none whitespace-pre-line rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t p-4">
          {readOnly ? (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Schliessen
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onSave(mail.id)}>
                <Save className="size-4" />
                Als Entwurf speichern
              </Button>
              <Button type="submit" form="outbound-form">
                <Send className="size-4" />
                {mail.status === 'fehler' ? 'Erneut senden' : 'Senden'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
